import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/apiFetch';
import { useApiRoot } from '../contexts/ApiRootContext';

export interface ClassificationEntry {
    classification_id: string;
    level_1: { name: string; code: string; description: string };
    level_2: { name: string; code: string };
    level_3: { name: string; code: string; description: string };
    disposal_recommendation: string;
    minimum_retention_period: string;
    retention_period_start_condition: string;
    note: string;
}

export interface ClassificationHierarchy {
    [level1: string]: {
        [level2: string]: ClassificationEntry[];
    };
}

let cachedHref: string | null = null;
let cachedHierarchy: ClassificationHierarchy | null = null;
let inflightHref: string | null = null;
let inflightPromise: Promise<ClassificationHierarchy> | null = null;

function buildHierarchy(entries: ClassificationEntry[]): ClassificationHierarchy {
    const grouped: ClassificationHierarchy = {};
    for (const entry of entries) {
        if (!grouped[entry.level_1.name]) grouped[entry.level_1.name] = {};
        if (!grouped[entry.level_1.name][entry.level_2.name]) grouped[entry.level_1.name][entry.level_2.name] = [];
        grouped[entry.level_1.name][entry.level_2.name].push(entry);
    }
    return grouped;
}

function fetchHierarchy(href: string): Promise<ClassificationHierarchy> {
    if (cachedHref === href && cachedHierarchy) {
        return Promise.resolve(cachedHierarchy);
    }
    if (inflightHref === href && inflightPromise) {
        return inflightPromise;
    }

    inflightHref = href;
    inflightPromise = apiFetch(href)
        .then(response => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
        })
        .then((data: ClassificationEntry[]) => {
            const grouped = buildHierarchy(data);
            cachedHref = href;
            cachedHierarchy = grouped;
            return grouped;
        })
        .catch(err => {
            if (inflightHref === href) {
                inflightHref = null;
                inflightPromise = null;
            }
            throw err;
        });

    return inflightPromise;
}

export function useRecordClassifications() {
    const { loading: rootLoading, getHref } = useApiRoot();
    const href = getHref('record_classifications');
    const [hierarchy, setHierarchy] = useState<ClassificationHierarchy>(
        href && cachedHref === href && cachedHierarchy ? cachedHierarchy : {},
    );
    const [loading, setLoading] = useState(Boolean(rootLoading || href));
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (rootLoading) {
            setLoading(true);
            return;
        }

        if (!href) {
            setHierarchy({});
            setLoading(false);
            setError(null);
            return;
        }

        if (cachedHref === href && cachedHierarchy) {
            setHierarchy(cachedHierarchy);
            setLoading(false);
            setError(null);
            return;
        }

        let cancelled = false;
        setLoading(true);

        fetchHierarchy(href)
            .then(grouped => {
                if (!cancelled) {
                    setHierarchy(grouped);
                    setLoading(false);
                    setError(null);
                }
            })
            .catch(err => {
                console.error('Failed to fetch record classifications:', err);
                if (!cancelled) {
                    setError('Failed to load classifications');
                    setLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, [rootLoading, href]);

    return { hierarchy, loading, error };
}
